import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import dayjs from "dayjs";
import ApiHelper from "../api/api_helper";

const ACTION_LABELS = {
  CREATE: "สร้างข้อมูล",
  IMPORT: "นำเข้าจาก Excel",
  UPDATE: "แก้ไขข้อมูล",
  APPROVE: "อนุมัติแบบฟอร์ม",
  REJECT: "ไม่อนุมัติแบบฟอร์ม",
  REQUEST_GATE_OUT: "ขอ Gate out",
  APPROVE_GATE_OUT: "อนุมัติ Gate out",
  REJECT_GATE_OUT: "ปฏิเสธ Gate out",
  CANCEL_GATE_OUT: "ยกเลิก Gate out",
  SWAP: "Swap Booking",
  UPLOAD_IMAGE: "อัปโหลดรูปภาพ",
  DELETE_IMAGE: "ลบรูปภาพ",
};

const FIELD_LABELS = {
  status: "Status (AV / RW / RJ)",
  isConfirm: "สถานะอนุมัติ",
  gateStatus: "สถานะ Gate",
  containerId: "Container ID",
  booking: "Booking",
  location: "Location",
  size: "Size",
  tareWeight: "Tare Weight",
  sealNo: "Seal No",
  license: "ทะเบียนรถ",
  containerType: "Container Type",
  trailer: "Trailer",
  truckLicense: "Truck License",
  surveyCyPlace: "Cy Place",
  approveDate: "วันที่ Gate In",
  gateOutDate: "วันที่ Gate Out",
  requestGateOutDate: "วันที่ Request Gate Out",
  remark: "Remark",
  remarkLayPaper: "Remark ปูกระดาษ",
  layPaper: "ปูกระดาษ",
  cyDate: "Cy Date",
  loadingDate: "Loading Date",
  closingDate: "Closing Date",
  closingTime: "Closing Time",
  ci: "CI",
  unit: "Unit",
  customer: "Customer",
  country: "Country",
  shippingLir: "Shipping Line",
  cyPlace: "Cy Place",
  cyPic: "Cy PIC",
  cyContact: "Cy Contact",
  planner: "Planner",
  bookingStatus: "Booking Status",
};

const STATUS_COLORS = {
  AV: "#4AAEAF",
  REWORK: "#F7B627",
  REJECT: "#ED5768",
};

const isoDatePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (isoDatePattern.test(value)) return dayjs(value).format("DD/MM/YYYY HH:mm");
  return value;
};

const renderValue = (field, value) => {
  if (field === "status" && STATUS_COLORS[value]) {
    return (
      <Chip
        size="small"
        label={value}
        sx={{ backgroundColor: STATUS_COLORS[value], color: "white" }}
      />
    );
  }
  return <span>{formatValue(value)}</span>;
};

// รวมรายการที่เกิดจากการกดบันทึกครั้งเดียวกันไว้เป็นแถวเดียว
const groupRows = (rows) => {
  const groups = [];
  const byId = {};
  rows.forEach((row) => {
    const key = row.groupId || `row-${row.id}`;
    if (!byId[key]) {
      byId[key] = { ...row, changes: [] };
      groups.push(byId[key]);
    }
    if (row.field) byId[key].changes.push(row);
  });
  return groups;
};

const AuditHistoryTable = ({ surveyFormId, booking, showContainer }) => {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setIsLoading(true);
        const response = surveyFormId
          ? await ApiHelper.get(`/survey/${surveyFormId}/history`)
          : await ApiHelper.get(`/booking/history`, { booking });
        if (response.status !== 200) {
          throw response;
        }
        setRows(response.data.data);
      } catch (err) {
        setError("ไม่สามารถดึงประวัติการแก้ไขได้");
      } finally {
        setIsLoading(false);
      }
    };
    fetchHistory();
  }, [surveyFormId, booking]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  if (rows.length === 0) {
    return <Typography color="text.secondary">ยังไม่มีประวัติการแก้ไข</Typography>;
  }

  const groups = groupRows(rows);

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 440 }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 130 }}>วันที่ / เวลา</TableCell>
            <TableCell sx={{ minWidth: 140 }}>ผู้ดำเนินการ</TableCell>
            <TableCell sx={{ minWidth: 120 }}>การดำเนินการ</TableCell>
            {showContainer && (
              <TableCell sx={{ minWidth: 120 }}>Container ID</TableCell>
            )}
            <TableCell sx={{ minWidth: 260 }}>รายละเอียด</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map((group) => (
            <TableRow key={group.groupId || group.id}>
              <TableCell sx={{ verticalAlign: "top" }}>
                {dayjs(group.createdAt).format("DD/MM/YYYY HH:mm:ss")}
              </TableCell>
              <TableCell sx={{ verticalAlign: "top" }}>
                <Typography fontSize={14}>{group.userFullname || "-"}</Typography>
                <Typography fontSize={12} color="text.secondary">
                  {group.username || ""}
                </Typography>
              </TableCell>
              <TableCell sx={{ verticalAlign: "top" }}>
                {ACTION_LABELS[group.action] || group.action}
              </TableCell>
              {showContainer && (
                <TableCell sx={{ verticalAlign: "top" }}>
                  {group.containerId || "-"}
                </TableCell>
              )}
              <TableCell sx={{ verticalAlign: "top" }}>
                {group.changes.length === 0 ? (
                  <Typography fontSize={14} color="text.secondary">
                    {group.action === "UPLOAD_IMAGE" ||
                    group.action === "DELETE_IMAGE"
                      ? formatValue(group.newValue || group.oldValue)
                      : "-"}
                  </Typography>
                ) : (
                  group.changes.map((change) => (
                    <Box
                      key={change.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 1,
                        fontSize: 14,
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        fontSize={14}
                        fontWeight={change.field === "status" ? "bold" : "normal"}
                      >
                        {FIELD_LABELS[change.field] || change.field}:
                      </Typography>
                      {change.action !== "CREATE" && (
                        <>
                          {renderValue(change.field, change.oldValue)}
                          <span>→</span>
                        </>
                      )}
                      {renderValue(change.field, change.newValue)}
                    </Box>
                  ))
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const AuditHistoryButton = ({ surveyFormId, booking, showContainer }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant="contained"
        startIcon={<HistoryIcon />}
        sx={{
          backgroundColor: "#002078",
          "&:hover": { backgroundColor: "#002078" },
          color: "white",
          borderRadius: "10px",
        }}
        onClick={() => setOpen(true)}
      >
        ประวัติการแก้ไข
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          ประวัติการแก้ไข {booking ? `Booking ${booking}` : ""}
        </DialogTitle>
        <DialogContent>
          {open && (
            <AuditHistoryTable
              surveyFormId={surveyFormId}
              booking={booking}
              showContainer={showContainer}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>ปิด</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AuditHistoryTable;
